/**
 * Reporte de incidentes de seguridad por colaboradores (/reportar-incidente):
 * modos de reporte, pasos del asistente, categorías, primeros pasos de
 * respaldo y construcción del registro. Lógica pura y configuración.
 */

/** Horas de plazo de respuesta sugeridas por severidad (ver incidentService). */
const RESPONSE_HOURS = { Crítico: 4, Alto: 24, Medio: 72, Bajo: 168 }

/** Modos de reporte (mismo patrón que /nuevo-ticket). */
export const INCIDENT_REPORT_MODES = [
  {
    key: 'guiado',
    title: 'Paso a paso',
    icon: 'fa-shoe-prints',
    color: '#14b8a6',
    description:
      'Le hacemos una pregunta a la vez y la IA le indica qué hacer de inmediato.',
    bullets: [
      'Una pregunta por pantalla',
      'Primeros pasos con IA antes de enviar',
      'Ideal si no es de TI',
    ],
    badge: 'Recomendado',
    showSidebar: false,
  },
  {
    key: 'formulario',
    title: 'Formulario avanzado',
    icon: 'fa-sliders',
    color: '#3b82f6',
    description:
      'Todos los campos en una sola vista, para quien ya conoce el proceso.',
    bullets: [
      'Más rápido si ya sabe qué poner',
      'Incluye un tutorial guiado',
      'Primeros pasos con IA al enviar',
    ],
    showSidebar: true,
  },
]

/** Busca un modo de reporte por su clave. */
export const getIncidentReportMode = (key) =>
  INCIDENT_REPORT_MODES.find((m) => m.key === key)

/** Tipos de incidente que ve el colaborador (orientan a la IA y al equipo). */
export const INCIDENT_CATEGORIES = [
  {
    key: 'phishing',
    label: 'Correo sospechoso o phishing',
    icon: 'fa-envelope-open-text',
  },
  { key: 'password', label: 'Contraseña comprometida', icon: 'fa-key' },
  { key: 'device', label: 'Pérdida o robo de equipo', icon: 'fa-laptop' },
  {
    key: 'leak',
    label: 'Información enviada a quien no debía',
    icon: 'fa-file-export',
  },
  { key: 'malware', label: 'Virus o comportamiento extraño', icon: 'fa-bug' },
  { key: 'access', label: 'Acceso no autorizado', icon: 'fa-user-lock' },
  { key: 'other', label: 'Otro', icon: 'fa-circle-question' },
]

export const getIncidentCategory = (key) =>
  INCIDENT_CATEGORIES.find((c) => c.key === key) || null

/** Explicación de cada severidad en lenguaje del colaborador. */
export const SEVERITY_HINTS = {
  Crítico: 'Está pasando ahora y afecta a muchas personas o datos sensibles.',
  Alto: 'Afecta información importante o puede empeorar pronto.',
  Medio: 'Un riesgo real, pero acotado a una persona o equipo.',
  Bajo: 'Algo sospechoso sin daño aparente.',
}

/** Pasos del asistente. `fields` se valida antes de avanzar. */
export const REPORT_WIZARD_STEPS = [
  {
    key: 'what',
    title: '¿Qué pasó?',
    subtitle: 'Elija el tipo que más se parece y cuéntenos con sus palabras.',
    icon: 'fa-triangle-exclamation',
    fields: ['category', 'description'],
  },
  {
    key: 'when',
    title: '¿Cuándo y cómo lo notó?',
    subtitle: 'Aproximado está bien.',
    icon: 'fa-clock',
    fields: ['detectedAt'],
  },
  {
    key: 'impact',
    title: '¿Qué tan grave parece?',
    subtitle: 'Si duda, elija el nivel más alto: el equipo lo ajustará.',
    icon: 'fa-gauge-high',
    fields: ['severity'],
  },
  {
    key: 'assets',
    title: '¿Qué equipos o sistemas están involucrados?',
    subtitle: 'Opcional. Marque los que conozca.',
    icon: 'fa-server',
    fields: [],
  },
  {
    key: 'firstSteps',
    title: 'Qué hacer ahora',
    subtitle: 'Mientras el equipo de seguridad lo revisa, siga estos pasos.',
    icon: 'fa-list-check',
    fields: [],
  },
  {
    key: 'review',
    title: 'Revise y envíe',
    subtitle: 'Confirme que todo esté correcto.',
    icon: 'fa-paper-plane',
    fields: ['category', 'description', 'detectedAt', 'severity'],
  },
]

/** Pasos del tutorial del formulario avanzado (`data-tour` en IncidentForm). */
export const INCIDENT_FORM_TOUR_STEPS = [
  {
    target: 'inc-detected',
    title: 'Fecha y severidad',
    body: 'Cuándo lo notó y qué tan grave parece. Si duda de la severidad, elija la más alta.',
  },
  {
    target: 'inc-source',
    title: 'Origen',
    body: 'Cómo se enteró: un correo, un aviso del antivirus, un compañero…',
  },
  {
    target: 'inc-description',
    title: 'Descripción',
    body: 'Qué ocurrió, a quién afectó y qué ha hecho hasta ahora. No borre evidencia (correos, archivos).',
  },
  {
    target: 'inc-assets',
    title: 'Activos afectados',
    body: 'Marque los equipos o sistemas involucrados, si los conoce.',
  },
  {
    target: 'inc-personal',
    title: 'Datos personales',
    body: 'Indique si pudo verse expuesta información de personas (clientes, colaboradores).',
  },
  {
    target: 'inc-submit',
    title: 'Enviar',
    body: 'Al enviar recibirá un código y los primeros pasos a seguir mientras el equipo lo revisa.',
  },
]

/** Borrador inicial del asistente. */
export const emptyIncidentDraft = (reportedBy = '', now = new Date()) => ({
  category: '',
  description: '',
  detectedAt: toLocalInput(now),
  detectionSource: '',
  severity: 'Medio',
  personalData: false,
  assetIds: [],
  reportedBy,
})

/**
 * Fecha para `<input type="datetime-local">` (hora local, sin segundos).
 * @param {Date|string} value
 * @returns {string}
 */
export function toLocalInput(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

/**
 * Valida campos del borrador. Devuelve campo → mensaje.
 * @param {Object} draft
 * @param {string[]} [fields]
 * @returns {Record<string, string>}
 */
export const validateIncidentDraft = (draft, fields) => {
  const errors = {}
  const check = (key) => !fields || fields.includes(key)
  if (check('category') && !draft.category)
    errors.category = 'Elija el tipo de incidente.'
  if (check('description') && draft.description.trim().length < 10)
    errors.description = 'Describa qué pasó (al menos una frase).'
  if (check('detectedAt') && !draft.detectedAt)
    errors.detectedAt = 'Indique cuándo lo notó.'
  if (check('severity') && !RESPONSE_HOURS[draft.severity])
    errors.severity = 'Elija la severidad.'
  return errors
}

/**
 * Construye el registro de `InventoryIT.SgsiIncident` desde el asistente.
 * El incidente entra "Abierto"; el tipo elegido encabeza la descripción para
 * que el equipo lo vea en el triage.
 *
 * @param {Object} draft
 * @returns {Object}
 */
export const buildIncidentPayload = (draft) => {
  const category = getIncidentCategory(draft.category)
  const detected = new Date(draft.detectedAt)
  const hours = RESPONSE_HOURS[draft.severity]
  return {
    DetectedAt: detected.toISOString(),
    ReportedBy: draft.reportedBy?.trim() || null,
    DetectionSource: draft.detectionSource?.trim() || null,
    Description: category
      ? `[${category.label}] ${draft.description.trim()}`
      : draft.description.trim(),
    Severity: draft.severity,
    ResponseDueAt: new Date(detected.getTime() + hours * 3600000).toISOString(),
    PersonalDataAffected: !!draft.personalData,
    Status: 'Abierto',
  }
}

/**
 * Primeros pasos de respaldo (si la IA no está disponible), por tipo.
 * Mismo formato que la respuesta de la IA.
 */
const COMMON_DONT = [
  'No borre correos, archivos ni mensajes: son evidencia.',
  'No intente investigar o "arreglarlo" por su cuenta.',
]

export const FALLBACK_FIRST_STEPS = {
  phishing: {
    steps: [
      {
        title: 'No haga clic ni responda',
        detail:
          'No abra enlaces ni adjuntos del correo y no responda al remitente.',
        urgent: true,
      },
      {
        title: 'Si ya hizo clic, cambie su contraseña',
        detail:
          'Cámbiela desde otro equipo de confianza y avise al equipo de TI.',
        urgent: true,
      },
      {
        title: 'Conserve el correo',
        detail:
          'No lo borre: el equipo de seguridad lo necesita para analizarlo.',
        urgent: false,
      },
    ],
    dont: COMMON_DONT,
  },
  password: {
    steps: [
      {
        title: 'Cambie la contraseña de inmediato',
        detail: 'Y en cualquier otro sistema donde usara la misma.',
        urgent: true,
      },
      {
        title: 'Cierre sesiones abiertas',
        detail: 'Cierre sesión en los dispositivos que no reconozca.',
        urgent: true,
      },
      {
        title: 'Revise actividad reciente',
        detail: 'Anote accesos o cambios que no reconozca para el equipo.',
        urgent: false,
      },
    ],
    dont: ['No comparta la nueva contraseña con nadie.', ...COMMON_DONT],
  },
  device: {
    steps: [
      {
        title: 'Avise a TI por teléfono ahora',
        detail: 'Para bloquear el equipo y sus accesos cuanto antes.',
        urgent: true,
      },
      {
        title: 'Cambie sus contraseñas',
        detail: 'Correo y sistemas a los que accedía desde ese equipo.',
        urgent: true,
      },
      {
        title: 'Si fue robo, haga la denuncia',
        detail: 'Y conserve el número de la denuncia para el reporte.',
        urgent: false,
      },
    ],
    dont: COMMON_DONT,
  },
  leak: {
    steps: [
      {
        title: 'Pida al destinatario que lo elimine',
        detail: 'Por escrito, sin reenviar la información.',
        urgent: true,
      },
      {
        title: 'Anote qué se envió y a quién',
        detail: 'Hora, destinatarios y tipo de información.',
        urgent: false,
      },
    ],
    dont: [
      'No vuelva a enviar la información para "corregir".',
      ...COMMON_DONT,
    ],
  },
  malware: {
    steps: [
      {
        title: 'Desconecte el equipo de la red',
        detail:
          'Quite el cable de red y apague el Wi-Fi, pero no apague el equipo.',
        urgent: true,
      },
      {
        title: 'Deje de usarlo',
        detail: 'No abra archivos ni conecte memorias USB.',
        urgent: true,
      },
      {
        title: 'Anote lo que vio',
        detail: 'Mensajes en pantalla, archivos raros, cuándo empezó.',
        urgent: false,
      },
    ],
    dont: COMMON_DONT,
  },
  access: {
    steps: [
      {
        title: 'Cambie sus contraseñas',
        detail: 'Empezando por el sistema donde vio el acceso.',
        urgent: true,
      },
      {
        title: 'Anote qué vio',
        detail: 'Fecha, sistema y qué acceso o cambio no reconoce.',
        urgent: false,
      },
    ],
    dont: COMMON_DONT,
  },
  other: {
    steps: [
      {
        title: 'Deténgase y conserve la evidencia',
        detail: 'No borre ni modifique nada relacionado.',
        urgent: true,
      },
      {
        title: 'Anote lo que sabe',
        detail: 'Qué pasó, cuándo y quién más lo vio.',
        urgent: false,
      },
    ],
    dont: COMMON_DONT,
  },
}

/**
 * Pasos de respaldo para una categoría (u "otro" si no se reconoce).
 * @param {string} category
 * @returns {{summary: string, steps: Array<{title: string, detail: string, urgent: boolean}>, dont: string[], source: 'fallback'}}
 */
export const fallbackFirstSteps = (category) => ({
  summary:
    'Estos son los pasos básicos para este tipo de incidente mientras el equipo de seguridad lo revisa.',
  ...(FALLBACK_FIRST_STEPS[category] || FALLBACK_FIRST_STEPS.other),
  source: 'fallback',
})
